import { useCallback, useEffect, useState } from "react";

import { ReasonChip, StockCount } from "@/components/chips";
import * as products from "@/lib/api/products";
import { ApiError } from "@/lib/api-client";
import { Pager, Select } from "@/routes/orders";
import type { AdminProduct, AdminStockMovement, StockSummary } from "@/lib/api/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * Stock — adjust inventory, and read the ledger that explains it.
 *
 * ── Why the form takes a delta ─────────────────────────────────────────────
 *
 * "Received 50 units" is the operation that actually happened, and it is safe
 * to apply concurrently. "Set stock to 50" is not: it discards any sale that
 * landed while the operator had this page open — the same read-then-write race
 * as PLAN.md §4.5.1, with a human in the middle instead of two transactions.
 * So the default mode is a signed delta, and the API refuses anything else.
 *
 * ── Then why is there a "counted" mode ─────────────────────────────────────
 *
 * Because a physical stock-take genuinely produces an absolute number, and an
 * operator holding one should not have to do subtraction in their head to use
 * this screen. It is still not a set: the API computes the delta *inside* the
 * transaction, against the balance at that instant. If two units sell between
 * the count and the save, the result is 48 rather than the 50 that was typed —
 * and this screen says so afterwards rather than letting it look like a bug.
 *
 * ── Why the ledger is on the same screen ───────────────────────────────────
 *
 * "Why does the system think we have four of these" is the question this screen
 * exists to answer, and a bare number cannot answer it. Every row of the ledger
 * is a movement `adjustStock()` wrote in the same transaction as the change
 * itself, which is what makes the count and the history incapable of drifting.
 */

type Scope = "low" | "all";

type State =
  | { phase: "loading" }
  | { phase: "ready"; rows: AdminProduct[]; total: number; pages: number }
  | { phase: "error"; message: string };

export function StockRoute() {
  const [scope, setScope] = useState<Scope>("low");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState<State>({ phase: "loading" });
  const [summary, setSummary] = useState<StockSummary | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  /**
   * Searching implies "all products", not "restock list filtered by name".
   * An operator typing a product name is looking for that product — being told
   * "no results" because it happens to be well stocked would be a lie about
   * the catalogue.
   */
  const effectiveScope: Scope = search.trim() ? "all" : scope;

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result =
          effectiveScope === "low"
            ? await products.fetchLowStock({ page }, controller.signal)
            : await products.fetchProducts(
                { page, search: search.trim() || undefined, sort: "stock" },
                controller.signal,
              );

        if (controller.signal.aborted) return;

        setState({
          phase: "ready",
          rows: result.data,
          total: result.meta.total,
          pages: Math.max(1, result.meta.pages),
        });
      } catch (thrown) {
        if (controller.signal.aborted) return;

        setState({
          phase: "error",
          message: thrown instanceof ApiError ? thrown.message : "Could not reach the API.",
        });
      }
    })();

    return () => controller.abort();
  }, [effectiveScope, search, page, reload]);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const fresh = await products.fetchStockSummary(controller.signal);
        if (!controller.signal.aborted) setSummary(fresh);
      } catch {
        // The tiles are context, not the screen. A failed summary must not take
        // the restock list down with it.
      }
    })();

    return () => controller.abort();
  }, [reload]);

  /**
   * Any adjustment re-reads everything rather than patching the row in place.
   * The applied delta may differ from the one submitted (a counted correction
   * lands against a balance that moved), so a locally-patched row would be a
   * guess — and this is the one screen where a stale number sends somebody to a
   * warehouse.
   */
  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-[20px] font-semibold text-ink">Stock</h1>
        <p className="mt-1 text-[13px] text-muted">
          Adjust inventory by delta and read the movement ledger behind every count.
        </p>
      </header>

      {summary ? <Summary summary={summary} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Product name or slug…"
          className="w-[240px] rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
        />

        <Select
          value={effectiveScope}
          onChange={(value) => {
            setScope(value as Scope);
            setPage(1);
          }}
          options={[
            { value: "low", label: "Needs restocking" },
            { value: "all", label: "All products" },
          ]}
        />

        {search.trim() && scope === "low" ? (
          <span className="text-[12px] text-faint">
            Searching every product, not just low ones.
          </span>
        ) : null}
      </div>

      {state.phase === "error" ? (
        <p className="rounded-lg bg-bad-tint px-4 py-3 text-[13px] text-bad">{state.message}</p>
      ) : state.phase === "loading" ? (
        <p className="text-[13.5px] text-muted">Loading…</p>
      ) : state.rows.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-4 py-8 text-center text-[13.5px] text-muted">
          {effectiveScope === "low"
            ? "Nothing is at or below its low-stock threshold."
            : "No products match that search."}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-line bg-surface">
            <table className="w-full border-collapse text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] tracking-wide text-faint uppercase">
                  <th className="px-4 py-3 font-semibold">Product</th>
                  <th className="px-4 py-3 font-semibold">Category</th>
                  {/* Count over threshold, in one cell: "4 / 5" is the whole
                      judgement an operator is making, and splitting it into two
                      columns makes them read across to make it. */}
                  <th className="px-4 py-3 font-semibold">On hand / low at</th>
                  <th className="px-4 py-3 font-semibold">Last change</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {state.rows.map((product) => (
                  <ProductRow
                    key={product.slug}
                    product={product}
                    expanded={open === product.slug}
                    onToggle={() =>
                      setOpen((current) => (current === product.slug ? null : product.slug))
                    }
                    onChanged={refresh}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <Pager page={page} pages={state.pages} onChange={setPage} />
        </>
      )}
    </div>
  );
}

/**
 * The tiles. `outOfStock` and `lowStock` never count the same product — the API
 * splits them at zero — so the two can be read as a total without adding a
 * product twice.
 */
function Summary({ summary }: { summary: StockSummary }) {
  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Tile
        label="Out of stock"
        value={String(summary.outOfStock)}
        note="Live products at zero"
        tone={summary.outOfStock > 0 ? "bad" : undefined}
      />
      <Tile
        label="Needs restocking"
        value={String(summary.lowStock)}
        note="At or below threshold"
        tone={summary.lowStock > 0 ? "warn" : undefined}
      />
      <Tile
        label="Catalogue"
        value={String(summary.active)}
        note={`${summary.draft} draft · ${summary.archived} archived`}
      />
      <Tile
        label="Movements · 24h"
        value={String(summary.movements24h)}
        note={`${summary.adjustments24h} by hand`}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: "warn" | "bad";
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <p className="text-[11px] font-bold tracking-wide text-faint uppercase">{label}</p>
      <p
        className={cn(
          "mt-1 text-[22px] font-bold tabular-nums",
          tone === "bad" ? "text-bad" : tone === "warn" ? "text-warn" : "text-ink",
        )}
      >
        {value}
      </p>
      {note ? <p className="mt-0.5 text-[11.5px] text-faint">{note}</p> : null}
    </div>
  );
}

function ProductRow({
  product,
  expanded,
  onToggle,
  onChanged,
}: {
  product: AdminProduct;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          "cursor-pointer border-b border-line last:border-0 hover:bg-canvas",
          expanded && "bg-canvas",
        )}
      >
        <td className="px-4 py-3">
          <span className="block font-medium text-ink">{product.name}</span>
          <span className="block text-[11.5px] text-faint">{product.slug}</span>
        </td>
        <td className="px-4 py-3 text-muted">{product.category.name}</td>
        <td className="px-4 py-3">
          <StockCount product={product} />
        </td>
        <td className="px-4 py-3 text-[12px] text-muted">{formatDateTime(product.updatedAt)}</td>
        <td className="px-4 py-3 text-right">
          <span className="text-[12px] font-semibold text-info">
            {expanded ? "Close" : "Adjust"}
          </span>
        </td>
      </tr>

      {expanded ? (
        <tr className="border-b border-line last:border-0">
          <td colSpan={5} className="bg-canvas px-4 py-4">
            <StockPanel slug={product.slug} onChanged={onChanged} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** The expanded row: the form on the left, the ledger it writes to on the right. */
function StockPanel({ slug, onChanged }: { slug: string; onChanged: () => void }) {
  const [log, setLog] = useState<AdminStockMovement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await products.fetchStockLog(slug, {}, controller.signal);
        if (!controller.signal.aborted) setLog(result.data);
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load the ledger.");
        }
      }
    })();

    return () => controller.abort();
  }, [slug, version]);

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      <AdjustForm
        slug={slug}
        onDone={() => {
          setVersion((n) => n + 1);
          onChanged();
        }}
      />

      <section>
        <h3 className="mb-2 text-[11px] font-bold tracking-wide text-faint uppercase">
          Movement ledger
        </h3>

        {error ? (
          <p className="text-[12.5px] text-bad">{error}</p>
        ) : log === null ? (
          <p className="text-[12.5px] text-muted">Loading…</p>
        ) : log.length === 0 ? (
          <p className="text-[12.5px] text-muted">No movements recorded yet for this product.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {log.map((movement) => (
              <Movement key={movement.id} movement={movement} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Movement({ movement }: { movement: AdminStockMovement }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px]">
      <span className="w-[130px] shrink-0 text-faint tabular-nums">
        {formatDateTime(movement.createdAt)}
      </span>

      <span
        className={cn(
          "w-[46px] shrink-0 text-right font-semibold tabular-nums",
          movement.delta > 0 ? "text-good" : "text-bad",
        )}
      >
        {movement.delta > 0 ? `+${movement.delta}` : movement.delta}
      </span>

      {/* The balance after the movement, straight from the ledger row rather
          than re-derived here. It is what reconciliation compares against. */}
      <span className="w-[38px] shrink-0 text-right text-faint tabular-nums">
        {movement.balance}
      </span>

      <ReasonChip reason={movement.reason} />

      <span className="min-w-0 flex-1 truncate text-muted">
        {movement.orderNumber ? (
          <span className="font-medium text-body">{movement.orderNumber}</span>
        ) : null}
        {movement.orderNumber && movement.note ? " · " : ""}
        {movement.note ?? ""}
      </span>
    </li>
  );
}

/**
 * The adjustment form.
 *
 * Two modes, one endpoint. The mode switch changes what the operator types, not
 * what the API does — it takes a delta either way, and computes one from a
 * count itself.
 */
function AdjustForm({ slug, onDone }: { slug: string; onDone: () => void }) {
  /**
   * No reset effect: the table keys each row by slug, so expanding a different
   * product unmounts this form and mounts a fresh one. A half-typed delta
   * cannot follow the operator to another product.
   */
  const [mode, setMode] = useState<"delta" | "counted">("delta");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const parsed = Number(amount);
  const valid =
    amount.trim() !== "" &&
    Number.isInteger(parsed) &&
    (mode === "delta" ? parsed !== 0 : parsed >= 0);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || busy) return;

    setBusy(true);
    setError(null);
    setResult(null);

    try {
      const adjustment = await products.adjustStock(slug, {
        ...(mode === "delta" ? { delta: parsed } : { countedQuantity: parsed }),
        // A count reconciles the system to the shelf; that is a correction by
        // definition, and the API insists on the pairing.
        reason: mode === "delta" ? "RESTOCK" : "CORRECTION",
        ...(note.trim() ? { note: note.trim() } : {}),
      });

      /**
       * The applied delta is echoed, not assumed. For a counted correction it
       * is computed server-side against a balance that may have moved since the
       * shelf was counted — so "counted 50, landed on 48" is a real and correct
       * outcome, and saying it here is the difference between an operator
       * trusting this screen and filing a bug against it.
       */
      const applied = adjustment.delta > 0 ? `+${adjustment.delta}` : String(adjustment.delta);

      setResult(
        mode === "counted" && adjustment.balance !== parsed
          ? `Applied ${applied}. Now ${adjustment.balance} — units sold while you were counting.`
          : `Applied ${applied}. Now ${adjustment.balance}.`,
      );

      setAmount("");
      setNote("");
      onDone();
    } catch (thrown) {
      setError(
        thrown instanceof ApiError ? thrown.message : "That adjustment could not be applied.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="rounded-lg border border-line bg-surface p-3"
    >
      <h3 className="mb-3 text-[11px] font-bold tracking-wide text-faint uppercase">
        Adjust stock
      </h3>

      <div className="mb-3 flex gap-1 rounded-lg bg-canvas p-1">
        {(
          [
            ["delta", "Add / remove"],
            ["counted", "Stock take"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setMode(value);
              setAmount("");
              setResult(null);
              setError(null);
            }}
            className={cn(
              "flex-1 rounded-md px-2 py-1.5 text-[12px] font-semibold transition-colors",
              mode === value ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <label htmlFor={`${slug}-amount`} className="mb-1 block text-[12px] font-medium text-body">
        {mode === "delta" ? "Units in or out" : "Units counted on the shelf"}
      </label>

      <input
        id={`${slug}-amount`}
        type="number"
        step={1}
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        placeholder={mode === "delta" ? "50, or -3" : "47"}
        className="mb-1.5 w-full rounded-lg border border-line bg-surface px-3 py-2 text-[13px] text-ink placeholder:text-faint"
      />

      <p className="mb-3 text-[11.5px] leading-relaxed text-faint">
        {mode === "delta"
          ? "A change, not a total. Positive puts units back on the shelf, negative takes them off."
          : "The number you counted. The system works out the difference at the moment it saves, so a sale that lands meanwhile is not erased."}
      </p>

      <label htmlFor={`${slug}-note`} className="mb-1 block text-[12px] font-medium text-body">
        Note
      </label>
      <input
        id={`${slug}-note`}
        type="text"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={mode === "delta" ? "Delivery from distributor" : "Optional"}
        className="mb-3 w-full rounded-lg border border-line bg-surface px-3 py-2 text-[13px] text-ink placeholder:text-faint"
      />

      {error ? <p className="mb-2 text-[12px] text-bad">{error}</p> : null}
      {result ? <p className="mb-2 text-[12px] font-medium text-good">{result}</p> : null}

      <button
        type="submit"
        disabled={!valid || busy}
        className="w-full rounded-lg bg-ink px-3 py-2 text-[12.5px] font-semibold text-white disabled:opacity-40"
      >
        {busy ? "Applying…" : mode === "delta" ? "Apply change" : "Record count"}
      </button>
    </form>
  );
}
